import { Component, Input, Output, EventEmitter } from '@angular/core';

@Component({
  selector: 'app-print',
  templateUrl: './print.component.html',
  styleUrls: ['./print.component.scss']
})
export class PrintComponent {

  @Input() mode: string;
  @Input() standard: string;
  @Input() popTitle: string;
  @Input() showBtn: boolean;
  @Input() btnText: string;
  @Input() btnClass: Record<string, any>;
  @Input() printHTML;
  @Input() printStyle: string;
  @Input() printCSS: string[];
  @Output() printComplete: EventEmitter<any>;

  private modes;
  private standards;
  private oldBtnText: string;
  private printWindow: Window;
  private printDoc: Document;

  constructor() {
    this.modes = {
      iframe: 'iframe',
      popup: 'popup'
    };
    this.standards = {
      strict: 'strict',
      loose: 'loose',
      html5: 'html5'
    };
    this.mode = this.modes.iframe;
    this.standard = this.standards.html5;
    this.popTitle = '';
    this.showBtn = true;
    this.btnClass = {
      'print-btn': true,
      'print-btn-success': true
    };
    this.popTitle = 'Print Window';
    this.btnText = 'Print';
    this.oldBtnText = this.btnText;
    this.printComplete = new EventEmitter<any>(false);
  }

  /**
   * Write Document
   */
  private write() {
    this.printDoc.open();
    this.printDoc.write(`${this.getBody()}`);
    this.printDoc.close();
  }

  /**
   * Obtain DOCTYPE
   * @returns {string}
   */
  private docType(): string {
    if (this.mode === this.modes.iframe) {
      return '';
    }
    if (this.standard === this.standards.html5) {
      return '<!DOCTYPE html>';
    }
    const transitional: string = this.standard === this.standards.loose ? 'Transitional' : '',
      dtd: string = this.standard === this.standards.loose ? 'loose' : 'strict';
    return `<!DOCTYPE HTML PUBLIC "-//W3C//DTD HTML 4.01 ${transitional}//EN" "http://www.w3.org/TR/html4/${dtd}.dtd">`;
  }

  /**
   * Obtain head
   * @returns {string}
   */
  private getHead(): string {
    let styles = '',
      links = '';

    // Print content css file
    if (this.printCSS) {
      this.printCSS.forEach((url) => {
        links += `<link href="${url}" rel="stylesheet">`;
      });
    }

    // Print content style
    if (this.printStyle) {
      styles = `<style>${this.printStyle}</style>`;
    }
    return `<head><title>${this.popTitle}</title>${styles}${links}</head>`;
  }

  /**
   * Obtain body
   * @returns {string}
   */
  private getBody() {
    let html = '';
    if (this.printHTML) {
      if (this.printHTML.outerHTML) {
        html = this.printHTML.outerHTML;
      } else {
        html = this.printHTML;
      }
    }
    return `<body><object id="eNgxPrintWB" style="display: none;" height="0"
    classid="clsid:8856F961-340A-11D0-A96B-00C04FD705A2"></object>${html}</body>`;
  }

  /**
   * Start printing
   */
  private startPrint() {
    const timeoutId = setTimeout(() => {
      this.printWindow.focus();
      // IE Browser
      if (!!window['ActiveXObject'] || 'ActiveXObject' in window) {
        try {
          // Add print preview under IE
          this.printWindow['eNgxPrintWB'].ExecWB(7, 1);
        } catch (e) {
          console.error(e);
        }
      } else {
        this.printWindow.print();
      }
      if (this.mode === this.modes.popup) {
        const timeoutId2 = setTimeout(() => {
          clearTimeout(timeoutId2);
          this.printWindow.close();
        }, 500);
      }
      clearTimeout(timeoutId);
      this.printComplete.emit();
      this.btnText = this.oldBtnText;
      this.setInputAndTextareaValue(true);
    }, 1000);
  }

  /**
   * Create iframe
   */
  private createIframe() {
    const oldFrame = document.getElementsByClassName('print-frame');
    if (oldFrame.length > 0) {
      oldFrame[0].parentNode.removeChild(oldFrame[0]);
    }
    try {
      const printIframe: HTMLIFrameElement = document.createElement('iframe');
      document.body.appendChild(printIframe);
      printIframe.style.position = 'absolute';
      printIframe.style.border = '0';
      printIframe.style.width = '0';
      printIframe.style.height = '0';
      printIframe.style.left = '0';
      printIframe.style.top = '0';
      printIframe.style.zIndex = '-1';
      printIframe.className = 'print-frame';
      this.printWindow = printIframe.contentWindow;
      this.printDoc = (printIframe.contentDocument ? printIframe.contentDocument : printIframe.contentWindow)
        ? printIframe.contentWindow.document
        : printIframe.contentDocument;
    } catch (e) {
      throw new Error(e);
    }

    if (!this.printWindow) {
      throw new Error('Cannot find window.');
    }

    if (!this.printDoc) {
      throw new Error('Cannot find document.');
    }
  }

  /**
   * Create popup
   */
  private createPopup() {
    let windowAttr = 'location=yes,statusbar=no,directories=no,menubar=no,titlebar=no,toolbar=no,dependent=no';
    windowAttr += `,width=${window.screen.width},height=${window.screen.height};`;
    windowAttr += ',resizable=yes,personalbar=no,scrollbars=yes';
    const newWin = window.open('', '_blank', windowAttr);
    this.printWindow = newWin;
    this.printDoc = newWin.document;
  }

  /**
   * Get print window
   */
  private getPrintWindow() {
    if (this.mode === this.modes.iframe) {
      this.createIframe();
    } else if (this.mode === this.modes.popup) {
      this.createPopup();
    }
  }

  /**
   * Start printing
   * @param printHTML Printed HTML
   */
  print(printHTML?) {
    this.printHTML = printHTML ? printHTML : this.printHTML;
    if (!this.printHTML.outerHTML) {
      const div: HTMLDivElement = document.createElement('div');
      div.innerHTML = this.printHTML;
      this.printHTML = div;
    }
    this.oldBtnText = this.btnText;
    this.btnText = 'Ready to print...';
    const timeoutId = setTimeout(() => {
      clearTimeout(timeoutId);
      this.setInputAndTextareaValue();
      this.getPrintWindow();
      this.write();
      this.startPrint();
    }, 1000);
  }

  /**
   * Set the value of the input box and text field in the printed document
   * @param {boolean} isReset whether to return to intial state
   */
  setInputAndTextareaValue(isReset = false) {
    const inputs = this.printHTML.getElementsByTagName('input');
    const excludeTypes: string[] = ['hidden', 'button', 'reset', 'submit'];
    const textareas = this.printHTML.getElementsByTagName('textarea');
    const selects = this.printHTML.getElementsByTagName('select');
    this.toArray(inputs).forEach((input: HTMLInputElement) => {
      if (excludeTypes.indexOf(input.type) < 0) {
        this.setInput(isReset, input);
      }
    });
    this.toArray(textareas).forEach((textarea: HTMLTextAreaElement) => {
      if (!isReset) {
        if (!textarea.innerHTML) {
          // Identifies whether innerHTML is set during printing and is used to determine whether to delete after printing
          textarea.setAttribute('isSetHtml', 'true');
          textarea.innerHTML = textarea.value;
        }
      } else {
        if (textarea.getAttribute('isSetHtml')) {
          textarea.innerHTML = '';
          textarea.removeAttribute('isSetHtml');
        }
      }
    });
    this.toArray(selects).forEach((select: HTMLSelectElement) => {
      this.toArray(select.selectedOptions).forEach((option: HTMLOptionElement) => {
        if (!isReset) {
          if (!option.getAttribute('selected')) {
            option.setAttribute('isSetValue', 'true');
            option.setAttribute('selected', 'true');
          }
        } else {
          if (option.getAttribute('isSetValue')) {
            option.removeAttribute('selected');
            option.removeAttribute('isSetValue');
          }
        }
      });
    });
  }

  private setInput(isReset: boolean, input: HTMLInputElement) {
    if (!isReset) {
      this.setAttributeValue(input);
    } else {
      this.resetValue(input);
    }
  }

  /**
   * Identifies whether value is set during printing, used to determine whether to delete after printing
   * @param input HTMLInputElement
   */
  private setAttributeValue(input: HTMLInputElement) {
    if (input.type === 'radio' || input.type === 'checkbox') {
      if (!input.getAttribute('checked') && input.checked) {
        input.setAttribute('isSetValue', 'true');
        input.setAttribute('checked', input.checked + '');
      }
    } else if (!input.getAttribute('value')) {
      input.setAttribute('isSetValue', 'true');
      input.setAttribute('value', input.value);
    }
  }

  private resetValue(input: HTMLInputElement) {
    if (input.type === 'radio' || input.type === 'checkbox') {
      if (input.getAttribute('isSetValue')) {
        input.removeAttribute('checked');
        input.removeAttribute('isSetValue');
      }
    } else if (input.getAttribute('isSetValue')) {
      input.removeAttribute('value');
      input.removeAttribute('isSetValue');
    }
  }
  /**
   * Convert array-like to array
   * @param arr
   * @returns {any[]}
   */
  toArray(arr) {
    return arr ? Array.prototype.slice.call(arr, 0) : [];
  }

}
